/**
 * 内部域名 → 外部（公网）域名映射。
 *
 * BSP 同一套后端同时挂在内网域名（*.momenta.works）和公网域名（*.momenta.cn）下，
 * 路径结构完全一致。内部用户在内网页面上拿到的链接外部供应商打不开，
 * 因此复制分享时需要把 host 换成对应的公网域名。
 */
const INTERNAL_TO_EXTERNAL_HOST: Record<string, string> = {
  'bsp.momenta.works': 'bsp.momenta.cn',
  'bsp-dev.momenta.works': 'bsp-dev.momenta.cn',
};

/**
 * 把任意 host 映射为外部可访问的 host。
 * 未在映射表中的 host（本地开发、已经是公网域名等）原样返回。
 */
export const toExternalHost = (host: string): string =>
  INTERNAL_TO_EXTERNAL_HOST[host] ?? host;

/**
 * 构造站内某个路径的外部访问链接。
 *
 * projectId 缺省时省略该查询参数。
 */
export const buildExternalUrl = (
  path: string,
  projectId?: number | string | null,
  origin: string = window.location.origin,
): string => {
  const url = new URL(path, origin);
  url.host = toExternalHost(url.host);
  // 本地开发（http://localhost:5173）保持 http，线上域名统一走 https
  if (url.host in INTERNAL_TO_EXTERNAL_HOST || Object.values(INTERNAL_TO_EXTERNAL_HOST).includes(url.host)) {
    url.protocol = 'https:';
  }
  if (projectId !== undefined && projectId !== null && projectId !== '') {
    url.searchParams.set('projectId', String(projectId));
  }
  return url.toString();
};

/**
 * 当前地址栏链接的外部版本：保留 path 与全部查询参数，只替换 host。
 *
 * 需求/设计材料/OPL 这类页面的链接内容随浏览位置变化（如 ?reqId=Req_Com_120），
 * 无法用固定路由模板拼出来，必须在点击那一刻读取真实地址栏。
 * 因此调用方要在事件处理里调用本函数，而不是在渲染时提前算好。
 */
export const buildExternalCurrentUrl = (
  href: string = window.location.href,
): string => {
  const url = new URL(href);
  url.host = toExternalHost(url.host);
  if (url.host in INTERNAL_TO_EXTERNAL_HOST || Object.values(INTERNAL_TO_EXTERNAL_HOST).includes(url.host)) {
    url.protocol = 'https:';
  }
  return url.toString();
};

/** 底软包外部访问链接：/package/<id>?projectId=<pid> */
export const buildExternalPackageUrl = (
  packageId: number | string,
  projectId?: number | string | null,
  origin?: string,
): string => buildExternalUrl(`/package/${packageId}`, projectId, origin);

/** Momenta 释放产物包外部访问链接：/external-release/<id>?projectId=<pid> */
export const buildExternalReleaseUrl = (
  releaseId: number | string,
  projectId?: number | string | null,
  origin?: string,
): string => buildExternalUrl(`/external-release/${releaseId}`, projectId, origin);
